import React, { cloneElement, useCallback } from 'react';
import { MetaTagsConfig } from 'react-metatags-hook/dist/cjs/types';
import { useSearchParams } from 'react-router-dom';
import { PostModel } from '../../data-layer/posts/types';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { Box } from '../../ui/layout/Box';
import { Stack } from '../../ui/layout/Stack';
import { PostAdCard } from '../../ui/posts/PostCard/PostAdCard';
import { PostCardSkeleton, PostCardWithVisibilityCheck } from '../../ui/posts/PostCard/PostCard';
import { PostContextMenu } from '../../ui/posts/PostContextMenu';
import { EventSource } from '../../utils/analyticsService';
import { buildImageSource } from '../../utils/buildImageSource';
import { getIsMobile } from '../../utils/getIsMobile';
import { useMeta } from '../../utils/useMeta';
import { useCurrentUser } from '../users/useCurrentUser';
import { useCanonicalUrl } from '../utils/useCanonicalUrl';
import { WithLoadMore } from '../withLoadMore';
import { FeedSnapScroll } from './FeedSnapScroll/FeedSnapScroll';
import { usePostCollection } from './usePostCollection';
import { withPostCallbacks } from './withPostCallbacks';

const PER_PAGE = 12;
const MOBILE_FOOTER_HEIGHT = 56;
const AD_INTERVAL = 5;
const AD_AD_TOP_FEED_OFFSET = 3;
const LOAD_MORE_INTERVAL = 10;

const EnhancedPostCard = withPostCallbacks(PostCardWithVisibilityCheck, EventSource.FEED);

const EnhancedPostMenu = withPostCallbacks(PostContextMenu, EventSource.FEED);

const buildMeta = (canonicalUrl: string, firstPost?: PostModel) => {
    const config: MetaTagsConfig = {};

    if (canonicalUrl) {
        config.links = [
            {
                rel: 'canonical',
                href: firstPost
                    ? canonicalUrl +
                      `businesses/${
                          firstPost.business ? firstPost.business.publicId : 'post'
                      }/posts/${firstPost.id}`
                    : canonicalUrl,
            },
        ];
    }

    if (!firstPost) {
        return config;
    }

    const {
        content: [
            {
                media: [firstMedia],
            },
        ],
    } = firstPost;

    const title = `${firstPost.text.split(/\s+/).slice(0, 5).join(' ')}... - ${
        firstPost.business ? firstPost.business.displayName : ''
    } - Яндекс Ритм`;

    config.openGraph = {
        title,
        description: firstPost.text,
        image:
            'previewFormats' in firstMedia
                ? buildImageSource(firstMedia.previewFormats)
                : 'firstFrameUrl' in firstMedia
                  ? firstMedia.firstFrameUrl
                  : firstMedia.url,
    };

    config.title = title;

    return config;
};

export function PostFeed() {
    const [query] = useSearchParams();
    const collection = usePostCollection({
        namespace: 'feed',
        filters: {
            amount: PER_PAGE,
            push_payload: query.get('push_payload'),
            first_post_id: query.get('first_post_id'),
            no_log: query.get('no_log'),
        },
    });
    const currentUser = useCurrentUser();
    const canonicalUrl = useCanonicalUrl();
    const isMobile = getIsMobile();

    useMeta(buildMeta(canonicalUrl, query.get('first_post_id') ? collection.items[0] : undefined));

    const getPost = useCallback(
        (index: number, item: PostModel) => (
            <Box width="100%">
                {item ? (
                    <EnhancedPostCard
                        post={item}
                        index={index}
                        renderMenu={(trigger) => (
                            <EnhancedPostMenu
                                index={index}
                                trigger={trigger}
                                post={item}
                                user={currentUser}
                            />
                        )}
                    />
                ) : (
                    <PostCardSkeleton />
                )}
            </Box>
        ),
        [currentUser],
    );

    const itemHandler = useCallback(
        (index: number) => {
            const item = collection.items[index];

            if ((index - AD_AD_TOP_FEED_OFFSET) % AD_INTERVAL === 0) {
                return (
                    <>
                        <PostAdCard postIndex={index} adInterval={AD_INTERVAL} />
                        {getPost(index, item)}
                    </>
                );
            }

            if (index !== 0 && index % LOAD_MORE_INTERVAL === 0) {
                return (
                    <WithLoadMore width="100%" onLoadMore={collection.onLoadNext}>
                        {getPost(index, item)}
                    </WithLoadMore>
                );
            }

            return getPost(index, item);
        },
        [collection, getPost],
    );

    if (isMobile) {
        return (
            <FeedSnapScroll
                items={collection.items}
                onLoadNext={collection.onLoadNext}
                heightOffset={MOBILE_FOOTER_HEIGHT}
            />
        );
    }

    return (
        <DashboardPage py={0}>
            <Stack
                width="100%"
                maxWidth="100%"
                count={collection.items.length}
                renderItem={(index) => cloneElement(itemHandler(index), { key: index })}
            />
        </DashboardPage>
    );
}
